import { Injectable } from '@angular/core';
import {
  ApiError,
  ArchitecturesService,
  BatteryFixedVoltages,
  BatteryLookupTableData,
  BatteryLookupTableID,
  ComponentFileType,
  ComponentInDB,
  ComponentsService as API,
  ConceptPopulated,
  InverterAnalytical,
  MotorLabID,
  MotorLossMap,
  MotorLossMapID,
  MotorTorqueCurves,
  TransmissionLossCoefficients,
  TransmissionLossMap,
  UnitType,
} from 'src/api';
import { ConceptPartType, PartServiceType } from '../types';
import { State } from '@ansys-internal/awf-angular/store';
import { PlotService } from './plot.service';
import { AWCTreeItem } from '@ansys-internal/awc-angular/trees';
import {
  ActiveSectionState,
  ActiveTabState,
  SectionSelectAction,
  SectionUpdateAction,
} from '@ansys-internal/awf-angular/workspace';
import { sectionState } from '../models/section-state';
import {
  AWCTableColumns,
  AWCTableRow,
  TableCellAlign,
  TableCellTypes,
} from '@ansys-internal/awc-angular/tables';
import { ConceptUnitService } from './unit.service';
import {
  ActionContributionsService,
  WorkspaceTabSection,
} from '@ansys-internal/awf-angular/contributions';
import {BehaviorSubject, lastValueFrom} from 'rxjs';
import { AnalyticEventEnum, ConceptInputType, ConceptSections } from '../enums';
import { SnackBarService } from '@ansys-internal/awf-angular/shared';
import { FileService } from './file.service';
import { ActiveConceptState } from 'src/app/state/lib/active-concept.state';
import { ComponentsState } from 'src/app/state/lib/components.state';
import { SelectedItemState } from 'src/app/state/lib/selected-item.state';
import { NotificationService } from './notification.service';
import {
  AnalyticsEventPayload,
  SubmitAnalyticsEventAction,
} from '@ansys-internal/awf-angular/analytics';
import { ScientificNotationPipe } from '../pipes/scientific-notation/scientific-notation.pipe';
import { HttpResponse } from '@angular/common/http';
import { PlotTypeEnum } from '../enums/plot-type.enum';
import { LossTypeEnum } from '../enums/loss-type.enum';
import {
  UploadFileResponseObjectType,
  UploadFileResponseType,
} from '../types/upload-file-response.type';
import { InverterLossesType } from '../enums/inverter-losses-type';
import { DownloadFilesAction } from '../../actions';
import { ComposeInverterLossesAction } from '../../actions/compose-invert-loss/compose-inverter-losses.action';
import { SelectedBatteryLookupColumnsState } from '../../state/lib/selected-battery-lookup-columns.state';
import { batteryLookupHeadersConst } from '../consts/battery-lookup-headers.const';
import { DisplayDataGraphResponseState } from '../../state/display-data-graph-response.state';
import {removeEmptyStrings} from "../tools/common.utils";
import {MotorTransmissionLossState} from "../../state/lib/motor-transmission-loss.state";
import {SelectedLossState} from "../../state/lib/selected-loss.state";

export type ComponentType =
  | BatteryFixedVoltages
  | MotorTorqueCurves
  | MotorLossMap
  | TransmissionLossCoefficients
  | TransmissionLossMap
  | InverterAnalytical;

export type MotorType = MotorLossMap | MotorLossMapID;

export interface LossMapGrid {
  losses: number[][];
  losses_total?: number[] | number[][];
  losses_iron?: number[][];
  speeds: number[];
  torques: number[];
  currents?: number[];
  phase_currents?: number[];
  phase_advances?: number[];
  slips?: number[];
  losses_switching?: number[];
  losses_conduction?: number[];
  losses_dc_harness?: number[];
  losses_ac_harness?: number[];
  dc_voltages?: number[];
  voltage_drops?: number[][];
  efficiencies?: number[][];
  generating_torques?: number[];
  generating_speeds?: number[];
}

export type DisplayDataOptions = {
  voltage?: number | null;
  gearRatio?: number;
  type?: string;
  lossType?: InverterLossesType | string;
  speed?: number;
  dc_current?: number | null;
  power_factor?: number | null;
  phase_current_max?: number | null;
  frequency?: number | null;
};

@Injectable({ providedIn: 'root' })
export class ComponentsService implements PartServiceType {
  private designId!: string;
  private activeSection!: string;
  public uploadState$ = new BehaviorSubject<boolean>(false);
  uploading$ = this.uploadState$.asObservable();

  constructor(
    private API: API,
    private state: State,
    private plotService: PlotService,
    private unitService: ConceptUnitService,
    private architectureAPI: ArchitecturesService,
    private notificationService: NotificationService,
    private uploadService: FileService,
    private snackbar: SnackBarService,
    private actions: ActionContributionsService,
    private unitChoices: ConceptUnitService,
    private snPipe: ScientificNotationPipe
  ) {
    this.state
      .get(ActiveConceptState)
      .subscribe((concept?: ConceptPopulated) => {
        if (concept) {
          this.designId = concept.design_id as string;
        }
      });
    this.activeSection = this.state.value(ActiveSectionState)?.type || '';
    this.state
      .get(ActiveSectionState)
      .subscribe((section?: WorkspaceTabSection) => {
        if (section) {
          this.activeSection = section.type;
        }
      });
  }

  public async addPart(
    item: ConceptPartType,
    file?: File,
    type?: ComponentFileType | string,
    uploadResponse?: UploadFileResponseType,
    thermalFilePart?: File
  ): Promise<ComponentInDB> {
    let resp: ComponentInDB;
    if (file) {
      resp = await this.addPartFromFile(
        item,
        file,
        type as ComponentFileType,
        uploadResponse
      );
    } else {
      resp = await lastValueFrom(
        this.API.createComponentsPost(
          {
            ...item,
          } as ComponentType,
          this.designId
        )
      ).catch((err: ApiError) => {
        const message = err.body?.message ?? 'Error Creating Component!';
        this.snackbar.error(message);
        throw new Error(err.body);
      });
    }

    const eventData: AnalyticsEventPayload = {
      type: AnalyticEventEnum.COMPONENT_ADDED,
      data: { component_type: resp.component_type },
    };
    this.actions.execute(SubmitAnalyticsEventAction, eventData);

    this.snackbar.success('Component Created!');
    return resp;
  }

  public async updatePart(item: AWCTreeItem): Promise<ComponentInDB> {
    const id = item.userData ? (item.userData['id'] as string) : null;
    if (!id) throw new Error('No ID provided');
    this.snackbar.info('Updating Component!');
    const resp = await lastValueFrom(
      this.API.updateComponentsItemIdPut(id, removeEmptyStrings(item.userData as ComponentType), this.designId)
    ).catch((err) => {
      this.notificationService.error('Error Updating Component', err);
      throw new Error(err);
    });
    item.text = resp.name;
    item.userData = {
      ...item.userData,
      ...resp,
    };

    this.toUpdateArchitecture(item);

    const tab = this.state.value(ActiveTabState);
    const currentState: AWCTreeItem[] = this.state.value(
      ComponentsState,
      tab?.id
    );
    const index = currentState.findIndex(
      (stateItem) => stateItem.id === item.id
    );
    currentState[index] = item;
    this.state.set(ComponentsState, currentState, tab?.id);

    if (item.userData?.['component_type']) {
      const eventData: AnalyticsEventPayload = {
        type: AnalyticEventEnum.COMPONENT_UPDATED,
        data: { config_type: item.userData?.['component_type'] },
      };
      this.actions.execute(SubmitAnalyticsEventAction, eventData);
    }

    this.snackbar.success('Component Updated!');
    return resp;
  }

  public async buildBatteryLookup(
    battery: BatteryLookupTableID
  ): Promise<[AWCTableColumns[], AWCTableRow[]]> {
    return new Promise<[AWCTableColumns[], AWCTableRow[]]>(async (resolve) => {
      const selectedColumnIds = this.state.value(
        SelectedBatteryLookupColumnsState
      );

      const columns: AWCTableColumns[] = selectedColumnIds
        .map((columnId) => {
          const headerConfig = batteryLookupHeadersConst.find(
            (header) => header.id === columnId
          );
          if (!headerConfig) return null;

          const unitType = this.getUnitTypeById(columnId);
          return {
            id: columnId,
            header: `${headerConfig.text} (${this.unitChoices.getChoice(
              unitType
            )})`,
            align: TableCellAlign.LEFT,
          } as AWCTableColumns;
        })
        .filter((column): column is AWCTableColumns => column !== null);

      const batteryData: BatteryLookupTableData = await lastValueFrom(
        this.API.calcDisplayDataComponentsGetDisplayDataPost(
          battery.id ?? '',
          this.designId
        )
      );

      const rows: AWCTableRow[] = batteryData.voltage.map((_, index) => {
        return {
          id: index.toString(),
          cells: selectedColumnIds.map((columnId) => {
            const cellValue = Array.isArray(
              batteryData[columnId as keyof BatteryLookupTableData]
            )
              ? (
                  batteryData[
                    columnId as keyof BatteryLookupTableData
                  ] as number[]
                )[index]
              : null;

            return {
              value: this.snPipe.transform(cellValue),
              type: TableCellTypes.NUMBER,
            };
          }),
        };
      });

      this.plotService.setTableData([columns, rows, [], []]);
      resolve([columns, rows]);
    });
  }

  private getUnitTypeById(columnId: string): UnitType {
    switch (columnId) {
      case 'voltage':
        return UnitType.VOLTAGE;
      case 'usable_charge':
        return UnitType.ELECTRICAL_ENERGY;
      case 'state_of_charge':
        return UnitType.RATIO;
      case 'charge_acceptance_limit':
      case 'charge_release_limit':
      case 'power_limit_charge':
      case 'power_limit_discharge':
        return UnitType.ELECTRICAL_POWER;
      case 'internal_resistance_charge':
      case 'internal_resistance_discharge':
        return UnitType.RESISTANCE;
      default:
        throw new Error(`Unknown column ID: ${columnId}`);
    }
  }

  public async displayData(
    displayPartId?: string,
    options?: DisplayDataOptions,
    useCachedResponse?: boolean
  ): Promise<void> {
    if (!displayPartId) {
      return;
    }

    await this.plotService.resetGraphLayout();

    if (!options?.lossType && (options?.type === ConceptInputType.TRANSMISSION || options?.type === ConceptInputType.MOTOR)) {
      const defaultLossType = this.state.value(MotorTransmissionLossState, displayPartId || '');
      if (defaultLossType) {
        options = {
          ...options,
          lossType: defaultLossType,
        };
      }
    }

    if (options?.lossType) {
      this.state.set(MotorTransmissionLossState, options.lossType, displayPartId);
    }

    if (useCachedResponse) {
      const cachedResp = this.state.value(DisplayDataGraphResponseState);
      await this.handleDisplayDataResponse(
        cachedResp,
        options || {},
        displayPartId || ''
      );

      return;
    }

    return new Promise<void>((resolve, reject) => {
      this.API.calcDisplayDataComponentsGetDisplayDataPost(
        displayPartId as string,
        this.designId,
        null,
        this.buildRequestPayload(options)
      ).subscribe({
        next: async (resp: LossMapGrid) => {
          await this.handleDisplayDataResponse(
            resp,
            options || {},
            displayPartId || ''
          );
          resolve();
        },
        error: () => reject(),
      });
    });
  }

  private async handleDisplayDataResponse(
    resp: LossMapGrid,
    options: DisplayDataOptions,
    displayPartId: string
  ): Promise<void> {
    this.state.set(DisplayDataGraphResponseState, resp);
    const { x, y, x2data, y2data, z } = this.getAxisData(resp, options);

    const data = this.buildPlotData(x, y, z, x2data, y2data, options);
    const layout = this.getLayout(resp, options);

    if (this.isCurrentSelection(displayPartId)) {
      this.plotService.setGraphData(data);
      await this.plotService.updateGraphLayout(layout);
    }
  }

  private buildRequestPayload(opts?: DisplayDataOptions): Record<string, any> {
    return {
      voltage: opts?.voltage,
      gear_ratio: opts?.gearRatio,
      speed: opts?.speed,
      dc_current: opts?.dc_current ?? undefined,
      power_factor: opts?.power_factor ?? undefined,
      phase_current_max: opts?.phase_current_max ?? undefined,
      frequency: opts?.frequency ?? undefined,
    };
  }

  private buildPlotData(
    x: any,
    y: any,
    z: any,
    x2data?: any,
    y2data?: any,
    options?: DisplayDataOptions
  ): any[] {
    const plotType = Array.isArray(z?.[0])
      ? PlotTypeEnum.CONTOUR
      : PlotTypeEnum.SCATTER;

    const commonData = {
      type: plotType,
      colorscale: 'Electric',
      x,
      y,
      z,
      colorbar: this.getColorBar(options || {}),
    };

    const y2 = y2data
      ? {
          ...commonData,
          x: x2data,
          y: y2data,
          line: { color: '#1f77b4' },
        }
      : {};

    return [commonData, y2];
  }

  private isCurrentSelection(displayPartId?: string): boolean {
    const activeSection = this.state.value(ActiveSectionState);
    const currentSelection = this.state.value(
      SelectedItemState,
      activeSection?.id
    );

    return (
      displayPartId === currentSelection?.id &&
      this.activeSection === ConceptSections.COMPONENT
    );
  }

  private getAxisData(
    resp: LossMapGrid,
    options?: DisplayDataOptions
  ): {
    x: number[];
    y: number[];
    x2data?: number[];
    y2data?: number[];
    z?: number[][] | undefined;
  } {
    if (!options) {
      return this.buildFallback(resp);
    }

    switch (options.type) {
      case ConceptInputType.INVERTER:
        return this.buildInverterAxes(resp, options);

      case ConceptInputType.MOTOR:
        return this.buildMotorAxes(resp, options);

      case ConceptInputType.TRANSMISSION:
        return this.buildTransmissionAxes(resp, options);

      default:
        return this.buildFallback(resp);
    }
  }

  private buildInverterAxes(resp: LossMapGrid, options: DisplayDataOptions) {
    let x: number[] = resp.phase_currents ?? resp.currents ?? [];
    let y: number[] = [];
    let z: number[][] | undefined;

    switch (options.lossType as InverterLossesType) {
      case InverterLossesType.SWITCHING:
        return { x: resp.currents ?? [], y: resp.losses_switching ?? [] };

      case InverterLossesType.CONDUCTION:
        return { x: resp.currents ?? [], y: resp.losses_conduction ?? [] };

      case InverterLossesType.DC_HARNESS:
        return { x: resp.currents ?? [], y: resp.losses_dc_harness ?? [] };

      case InverterLossesType.AC_HARNESS:
        return { x: resp.currents ?? [], y: resp.losses_ac_harness ?? [] };

      case InverterLossesType.TOTAL_LOSSES:
        return { x: resp.currents ?? [], y: (resp.losses_total as number[]) ?? [] };

      case InverterLossesType.LOSSES:
        return { x: resp.phase_currents ?? [], y: resp.dc_voltages ?? [], z: resp.losses };

      case InverterLossesType.VOLTAGE_DROPS:
        return { x: resp.phase_currents ?? [], y: resp.dc_voltages ?? [], z: resp.voltage_drops };
    }
  }

  private buildMotorAxes(resp: LossMapGrid, options: DisplayDataOptions) {
    let x: number[] = [];
    let y: number[] = [];
    let z: number[][] | undefined;
    let x2data: number[] | undefined;
    let y2data: number[] | undefined;

    switch (options.lossType) {
      case LossTypeEnum.IRON:
        x = resp.currents ?? [];
        y = resp.phase_advances ?? [];
        z = resp.losses_iron ?? [];
        break;

      case LossTypeEnum.TOTAL_LOSSES:
        x = resp.currents ?? resp.speeds ?? [];
        y = resp.phase_advances ?? resp.torques ?? [];
        z = (resp.losses_total as number[][]) ?? resp.losses ?? [];
        break;

      case LossTypeEnum.EFFICIENCIES:
        ({ x, y, z } = this.buildEfficiencyIsland(resp));
        break;

      default:
        x = resp.currents ?? resp.speeds ?? [];
        y = resp.phase_advances ?? resp.torques ?? [];
        z = (resp.losses_total as number[][]) ?? resp.losses ?? [];
        break;
    }

    if (Array.isArray(resp.generating_torques) && Array.isArray(resp.generating_speeds)) {
      y2data = resp.generating_torques;
      x2data = resp.generating_speeds;
    }

    return { x, y, z, x2data, y2data };
  }

  private buildEfficiencyIsland(resp: LossMapGrid) {
    const x = resp.speeds ?? [];
    const y = resp.torques ?? [];
    const eff = resp.efficiencies ?? [];

    if (!eff.length) return { x, y, z: [] };

    const flat = eff.flat();
    const maxEff = Math.max(...flat);
    const threshold = maxEff * 0.85;

    // Clip values below threshold to threshold
    const clipped = eff.map(row =>
      row.map(v => {
        if (v == null || Number.isNaN(v)) return NaN;        // true missing data
        return v >= threshold ? v : threshold;               // low values → threshold
      })
    );

    // Find rows/cols that contain values >= threshold
    const validRows = clipped
      .map((row, i) => (row.some(v => v > threshold) ? i : -1))
      .filter(i => i !== -1);

    const validCols = clipped[0]
      .map((_, j) => (clipped.some(row => row[j] > threshold) ? j : -1))
      .filter(j => j !== -1);

    if (!validRows.length || !validCols.length) {
      return { x, y, z: [] };
    }

    const rowMin = Math.min(...validRows);
    const rowMax = Math.max(...validRows);
    const colMin = Math.min(...validCols);
    const colMax = Math.max(...validCols);

    return {
      x: x.slice(colMin, colMax + 1),
      y: y.slice(rowMin, rowMax + 1),
      z: clipped
        .slice(rowMin, rowMax + 1)
        .map(row => row.slice(colMin, colMax + 1)),
    };
  }

  private buildTransmissionAxes(resp: LossMapGrid, options: DisplayDataOptions) {
    let x: number[] = [];
    let y: number[] = [];
    let z: number[][] | undefined;

    switch (options.lossType) {
      case LossTypeEnum.EFFICIENCIES:
        ({ x, y, z } = this.buildEfficiencyIsland(resp));
        break;

      case LossTypeEnum.TOTAL_LOSSES:
      case LossTypeEnum.LOSSES:
        x = resp.speeds ?? [];
        y = resp.torques ?? [];
        z = resp.losses ?? [];
        break;

      case LossTypeEnum.IRON:
        x = resp.speeds ?? [];
        y = resp.torques ?? [];
        z = resp.losses_iron ?? [];
        break;

      default:
        x = resp.speeds ?? [];
        y = resp.torques ?? [];
        z = resp.losses ?? [];
        break;
    }

    return { x, y, z };
  }

  private buildFallback(resp: LossMapGrid) {
    return {
      x: resp.speeds ?? [],
      y: resp.phase_advances ?? resp.slips ?? resp.torques ?? [],
      z: undefined,
    };
  }

  private getColorBar(options: DisplayDataOptions) {
    if (options.type === ConceptInputType.INVERTER) {
      switch (options.lossType) {
        case InverterLossesType.LOSSES:
          return {
            title: `Losses (${this.unitService.getChoice(UnitType.POWER)})`,
          };
        case InverterLossesType.VOLTAGE_DROPS:
          return {
            title: `Voltage (${this.unitService.getChoice(UnitType.VOLTAGE)})`,
          };
      }
    } else if (options.type === ConceptInputType.TRANSMISSION) {
      switch (options.lossType) {
        case LossTypeEnum.LOSSES:
        case LossTypeEnum.TOTAL_LOSSES:
          return {
            title: `Losses (${this.unitService.getChoice(UnitType.TORQUE)})`,
          };
        case LossTypeEnum.IRON:
          return {
            title: `Iron Losses (${this.unitService.getChoice(UnitType.TORQUE)})`,
          };
        case LossTypeEnum.EFFICIENCIES:
          return {
            title: `Efficiencies (${this.unitService.getChoice(UnitType.RATIO)})`,
          }
      }
    } else if (options.type === ConceptInputType.MOTOR) {
      switch (options.lossType) {
        case LossTypeEnum.LOSSES:
        case LossTypeEnum.TOTAL_LOSSES:
          return {
            title: `Losses (${this.unitService.getChoice(UnitType.POWER)})`,
          };
        case LossTypeEnum.IRON:
          return {
            title: `Iron Losses (${this.unitService.getChoice(UnitType.POWER)})`,
          };
        case LossTypeEnum.EFFICIENCIES:
          return {
            title: `Efficiencies (${this.unitService.getChoice(UnitType.RATIO)})`,
          }
      }
    }

    return {
      title: `Loss (${this.unitService.getChoice(
        options.type === 'transmission' ? UnitType.TORQUE : UnitType.POWER
      )})`,
    }
  }

  private getLayout(resp: LossMapGrid, opts?: DisplayDataOptions): any {
    let yTitle = '';
    if (resp.phase_advances) {
      yTitle = `Phase Advance (${this.unitService.getChoice(
        UnitType.GRADIENT
      )})`;
    } else if (resp.slips) {
      yTitle = 'Slip';
    } else {
      yTitle = `Torque (${this.unitService.getChoice(UnitType.TORQUE)})`;
    }

    if (opts?.type === ConceptInputType.INVERTER) {
      let yUnit = UnitType.POWER;
      switch (opts?.lossType) {
        case InverterLossesType.SWITCHING:
          yTitle = 'Switching Loss';
          break;
        case InverterLossesType.CONDUCTION:
          yTitle = 'Conduction Loss';
          break;
        case InverterLossesType.AC_HARNESS:
          yTitle = 'AC Harness Loss';
          break;
        case InverterLossesType.DC_HARNESS:
          yTitle = 'DC Harness Loss';
          break;
        case InverterLossesType.LOSSES:
        case InverterLossesType.VOLTAGE_DROPS:
          yTitle = 'DC Voltage'; // The y-axis title remains the same for both losses and voltage drops since the zAxis (colour bar) will contain the losses or voltage drops.
          yUnit = UnitType.VOLTAGE;
          break;
        default:
          yTitle = 'Total Loss';
          break;
      }

      yTitle += ` (${this.unitService.getChoice(yUnit)})`;
    }

    let showlegend = true;
    if (
      (opts?.type === ConceptInputType.MOTOR &&
        !resp.currents &&
        !resp.phase_currents &&
        !resp.phase_advances &&
        !resp.slips) ||
      opts?.type === ConceptInputType.INVERTER
    ) {
      showlegend = false;
    }

    const currentLabel = `Current (${this.unitService.getChoice(
      UnitType.CURRENT
    )})`;

    return {
      xaxis: {
        range: [],
        title:
          resp.currents || resp.phase_currents
            ? `${
                opts?.type === ConceptInputType.INVERTER ? 'Phase' : 'Stator'
              } ${currentLabel}`
            : `Speed (${this.unitService.getChoice(UnitType.ANGULAR_SPEED)})`,
      },
      yaxis: {
        range: [],
        title: yTitle,
      },
      showlegend,
    };
  }

  public async exportInverterData(
    displayPartId?: string,
    opts?: DisplayDataOptions
  ): Promise<void> {
    const lossData = await lastValueFrom(
      this.API.calcDisplayDataComponentsGetDisplayDataPost(
        displayPartId as string,
        this.designId,
        null, // @Deprecating
        {
          voltage: opts?.voltage,
          gear_ratio: opts?.gearRatio,
          speed: opts?.speed,
          dc_current: opts?.dc_current || undefined,
          power_factor: opts?.power_factor || undefined,
          phase_current_max: opts?.phase_current_max || undefined,
          frequency: opts?.frequency || undefined,
        }
      )
    );

    const csvContent = await this.actions.execute(ComposeInverterLossesAction, [
      lossData,
    ]);

    await this.actions.execute(DownloadFilesAction, [
      'Inverter Losses Data.csv',
      csvContent,
    ]);
  }

  public deletePart(item: AWCTreeItem): Promise<unknown> {
    return new Promise((resolve, reject) => {
      const id = item.userData ? (item.userData['id'] as string) : null;
      if (!id) throw new Error('No ID provided');
      this.API.deleteComponentsItemIdDelete(id, this.designId).subscribe(
        () => {
          const tab = this.state.value(ActiveTabState);
          const currentState: AWCTreeItem[] = this.state.value(
            ComponentsState,
            tab?.id
          );
          const index = currentState.findIndex(
            (stateItem) => stateItem.id === item.id
          );
          currentState.splice(index, 1);
          this.state.set(ComponentsState, currentState, tab?.id);

          if (item.userData?.['component_type']) {
            const eventData: AnalyticsEventPayload = {
              type: AnalyticEventEnum.COMPONENT_DELETED,
              data: { config_type: item.userData?.['component_type'] },
            };
            this.actions.execute(SubmitAnalyticsEventAction, eventData);
          }

          this.snackbar.success('Component Deleted!');
          resolve({});
        },
        (err) => {
          const message = err.body?.message ?? 'Error Deleting Component!';
          this.snackbar.error(message);
          reject();
          throw new Error(err);
        }
      );
    });
  }

  public async addThermalModelFile(
    dataId: string,
    thermalFile: File
  ): Promise<any> {
    return await lastValueFrom(
      this.API.addThermalModelComponentsThermalModelPost(
        dataId,
        { file: thermalFile },
        this.designId
      )
    ).catch((err) => {
      this.notificationService.error('Error Creating Component', err);
      throw new Error(err);
    });
  }

  private async addPartFromFile(
    item: ConceptPartType,
    file: File,
    type?: ComponentFileType,
    uploadResponse?: UploadFileResponseType
  ): Promise<ComponentInDB> {
    const name = item.name || 'New Component';
    let id;
    let responseObject!: UploadFileResponseObjectType;

    if (!uploadResponse) {
      const uploadResp = await this.uploadFilePart(file, type);
      [id, responseObject] = uploadResp.body;
    } else {
      [id, responseObject] = [uploadResponse.id, uploadResponse.responseObject];
    }

    const resp = await lastValueFrom(
      this.API.createComponentsPost(
        {
          ...item,
          name,
          mass: (item as MotorType).mass,
          cost: (item as MotorType).cost,
          component_type: (item as MotorLossMapID).component_type,
          data_id: id as string,
          control_strategy_bpm:
            (item as MotorLabID).control_strategy_bpm ??
            responseObject?.control_strategy_bpm,
          control_strategy_sync:
            (item as MotorLabID).control_strategy_sync ??
            responseObject?.control_strategy_sync,
          max_speed:
            (item as MotorLabID).max_speed ?? responseObject?.max_speed,
          rotor_temp:
            (item as MotorLabID).rotor_temp ?? responseObject?.rotor_temp,
          stator_current_limit:
            (item as MotorLabID).stator_current_limit ??
            responseObject?.stator_current_limit,
          stator_winding_temp:
            (item as MotorLabID).stator_winding_temp ??
            responseObject?.stator_winding_temp,
        } as never,
        this.designId
      )
    ).catch((err) => {
      this.notificationService.error('Error Creating Component', err);
      throw new Error(err);
    });
    this.snackbar.success('Component Created!');
    return resp;
  }

  public async uploadFilePart(
    file: File,
    type?: ComponentFileType
  ): Promise<HttpResponse<any>> {
    return await lastValueFrom(
      this.uploadService.uploadFile(
        type as ComponentFileType,
        this.designId,
        file
      )
    ).catch((err) => {
      this.notificationService.error('Error Uploading File', err);
      throw new Error(err);
    });
  }

  private async toUpdateArchitecture(item: AWCTreeItem): Promise<void> {
    const concept = this.state.value(ActiveConceptState);
    const architecture = concept?.architecture;
    let toUpdateArchitecture = false;
    if (!architecture) return;

    if (item?.userData?.['_parentID'] === ConceptInputType.TRANSMISSION) {
      toUpdateArchitecture =
        architecture.front_transmission_id === item.id ||
        architecture.rear_transmission_id === item.id;
    }
    if (item?.userData?.['_parentID'] === ConceptInputType.MOTOR) {
      toUpdateArchitecture =
        architecture.front_motor_id === item.id ||
        architecture.rear_motor_id === item.id;
    }
    if (item?.userData?.['_parentID'] === ConceptInputType.BATTERY) {
      toUpdateArchitecture = architecture.battery_id === item.id;
    }
    if (item?.userData?.['_parentID'] === ConceptInputType.INVERTER) {
      toUpdateArchitecture =
        architecture.front_inverter_id === item.id ||
        architecture.rear_inverter_id === item.id;
    }
    if (item?.userData?.['_parentID'] === ConceptInputType.CLUTCH) {
      toUpdateArchitecture =
        architecture.front_clutch_id === item.id ||
        architecture.rear_clutch_id === item.id;
    }
    if (toUpdateArchitecture) {
      concept.architecture = await lastValueFrom(
        this.architectureAPI.updateArchitectureArchitecturesItemIdPut(
          architecture.id as string,
          architecture,
          this.designId
        )
      );
      this.state.set(ActiveConceptState, concept);
    }
  }

  public shouldUpdateSection(item: ConceptPartType, parent?: string): boolean {
    const tab = this.state.value(ActiveTabState);
    const currentState = this.state.value(ComponentsState, tab?.id || '') || [];
    const transmissionCheck =
      currentState.find(
        (part: AWCTreeItem) =>
          part.userData && part.userData['_parentID'] === 'transmission'
      ) || parent === 'transmission';
    const motorCheck =
      currentState.find(
        (part: AWCTreeItem) =>
          part.userData && part.userData['_parentID'] === 'motor'
      ) || parent === 'motor';
    const batteryCheck =
      currentState.find(
        (part: AWCTreeItem) =>
          part.userData && part.userData['_parentID'] === 'battery'
      ) || parent === 'battery';

    if (transmissionCheck && motorCheck && batteryCheck) {
      sectionState.architecture = true;
      this.actions.execute(SectionUpdateAction);
    }
    return !!(transmissionCheck && motorCheck && batteryCheck);
  }

  /**
   * Gets component relative to the config id and design id
   * @param {string} id - ID of the component to get
   * @param {string} designId - ID of the design to get the component from
   * @returns {void}
   */
  public async get(id: string, designId: string): Promise<ComponentInDB> {
    return await lastValueFrom(this.API.readComponentsItemIdGet(id, designId));
  }
}
